import { capitalize } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { DealType } from "src/business/deal";
import { API_URL } from "src/Utils/API";
import { RestaurantReadResponse } from "../../types/restaurant";
import { mapRestrauntReadResponseToRestaurantDetails } from "../../restaurant/mappers/restaurantMapper";
import { RestaurantDetails } from "../../../business/restaurant/restaurant";

export interface PublicRestaurantTag {
  id: string;
  name: string;
}

export interface PublicRestaurantInfo {
  id: string;
  name: string;
  city: string;
  address: string;
  phone: string;
  googleRating: number;
  reviewCount: number;
  description: string;
  coverUrl: string;
  bookingDuration: number;
  openingDisplay: string;
  live: boolean;
  // Added by the backend to the public restaurant-info response. Optional until
  // that ships — the menu page's tags row is omitted when absent.
  tags?: PublicRestaurantTag[];
  // Largest party the restaurant accepts on a single booking. `null` — or absent
  // until the backend field ships — means it sets no limit.
  maximumPartySize?: number | null;
}

export interface PublicDeal {
  id: string;
  // DEAL or EVENT, used to attribute the booking. Backend must expose this on
  // the public deal DTO; until then it may be absent at runtime (treated as no
  // type when sending the booking).
  type: DealType;
  description: string;
  startTime: string;
  endTime: string;
  applicableDays: string[];
}

export const useGetPublicRestaurant = (name: string, city: string) =>
  useQuery({
    queryKey: ["public-restaurant", name, city],
    queryFn: async (): Promise<PublicRestaurantInfo> => {
      const res = await fetch(
        `${API_URL}/restaurants/public/${capitalize(city)}/${name}`,
      );
      if (res.status === 404) throw new Error("NOT_FOUND");
      const data = await res.json();
      if (data.success) return data.data as PublicRestaurantInfo;
      throw new Error("Failed to fetch restaurant");
    },
    enabled: !!name && !!city,
  });

export const useGetPublicRestaurantSchedule = (restaurantId?: string) =>
  useQuery({
    queryKey: ["public-restaurant-schedule", restaurantId],
    queryFn: async (): Promise<RestaurantDetails> => {
      const res = await fetch(
        `${API_URL}/restaurants/${restaurantId}/schedule`,
      );
      const data = await res.json();
      if (data.success)
        return mapRestrauntReadResponseToRestaurantDetails(
          data.data as RestaurantReadResponse,
        );
      throw new Error("Failed to fetch schedule");
    },
    enabled: !!restaurantId,
  });

export const useGetPublicDeals = (restaurantId?: string) =>
  useQuery({
    queryKey: ["public-restaurant-deals", restaurantId],
    queryFn: async (): Promise<PublicDeal[]> => {
      const res = await fetch(
        `${API_URL}/restaurants/${restaurantId}/deals/public`,
      );
      const data = await res.json();
      if (data.success) return data.data as PublicDeal[];
      return [];
    },
    enabled: !!restaurantId,
  });
